import React, { useEffect, useMemo, useRef, useState } from 'react';
import { X } from 'lucide-react';
import axiosConn from '../../axiosConn';
import { radioOutcomeSession } from './radioParticipationIntent';
import './hitLab.css';

const TIP_AMOUNTS_JGK = Object.freeze([10, 25, 100]);
const SUPPORT_NOT_SETUP = 'Artist support is not set up for this record yet.';

const cleanText = (value, max = 240) =>
  String(value || '').trim().slice(0, max);

const getTrackId = (track = {}) =>
  cleanText(track.trackId || track.id, 120);

const getTrackTitle = (track = {}) =>
  cleanText(track.title || track.songTitle || track.label, 160);

const getArtistName = (track = {}) =>
  cleanText(track.artistName || track.title || 'Joe Galaxy Radio', 120);

const getErrorMessage = (err, fallback) => {
  const message = cleanText(err?.response?.data?.message, 240);
  if (/insufficient/i.test(message)) {
    return 'Insufficient JGK balance for this action.';
  }
  return message || fallback;
};

const createClientRequestId = (prefix, trackId) => {
  const random =
    window.crypto?.randomUUID?.() ||
    `${Date.now()}-${Math.random().toString(36).slice(2, 10)}`;
  return ['hitlab-radio', prefix, trackId, random].join(':');
};

function DownloadLink({ href }) {
  if (!href) return null;
  return (
    <a className="hitlab-radio-support-download" href={href}>
      Download MP3
    </a>
  );
}

export default function HitLabRadioJgkSupportSheet({
  open = false,
  mode = 'tip',
  track = null,
  player = null,
  variant = 'full',
  onClose,
  onLogin,
  onSuccess,
}) {
  const dialogRef = useRef(null);
  const [amountJgk, setAmountJgk] = useState(25);
  const [submitting, setSubmitting] = useState(false);
  const [errorMessage, setErrorMessage] = useState('');
  const [result, setResult] = useState(null);

  const trackId = getTrackId(track);
  const trackTitle = getTrackTitle(track);
  const artistName = getArtistName(track);
  const isBuy = mode === 'buy';
  const priceJgk = Number(track?.buyPriceJgk || 0);
  const canTip = track?.canTipWithJgk === true;
  const canBuy =
    track?.canBuyWithJgk === true || track?.canBuySupportWithJgk === true;
  const alreadyPurchased = track?.alreadyPurchased === true;
  const downloadAvailable = track?.downloadAvailable === true;
  const unavailableReason = cleanText(
    isBuy ? track?.buyUnavailableReason : track?.tipUnavailableReason,
    180,
  );
  const actionAvailable = isBuy ? canBuy : canTip;
  const showPurchasedState = isBuy && alreadyPurchased && !canBuy;
  const supportUnavailable =
    !actionAvailable && !showPurchasedState
      ? unavailableReason || SUPPORT_NOT_SETUP
      : '';
  const title = isBuy ? 'Buy / Support with JGK' : 'Tip with JGK';
  const copy = isBuy
    ? 'Buy/support this record inside JG Universe and receive a one-time MP3 download.'
    : 'Send a JGK tip to support this record inside JG Universe.';
  const confirmLabel = isBuy ? 'Buy with JGK' : 'Send Tip';
  const endpoint = useMemo(() => {
    if (!trackId) return '';
    return isBuy
      ? `hitlab/radio/tracks/${encodeURIComponent(trackId)}/purchase`
      : `hitlab/radio/tracks/${encodeURIComponent(trackId)}/tip`;
  }, [isBuy, trackId]);

  useEffect(() => {
    if (!open) return;
    setAmountJgk(25);
    setSubmitting(false);
    setErrorMessage('');
    setResult(null);
    window.setTimeout(() => dialogRef.current?.focus?.(), 0);
  }, [open, mode, trackId]);

  useEffect(() => {
    if (!open) return undefined;
    const handleKeyDown = (event) => {
      if (event.key === 'Escape') onClose?.();
    };
    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [onClose, open]);

  if (!open || !trackId) return null;

  const submit = async () => {
    if (!player?.token || !endpoint || supportUnavailable || submitting) return;
    setSubmitting(true);
    setErrorMessage('');
    setResult(null);
    try {
      const clientRequestId = createClientRequestId(isBuy ? 'buy' : 'tip', trackId);
      const payload = isBuy
        ? { clientRequestId }
        : { amountJgk, clientRequestId };
      payload.participationListenSessionId = radioOutcomeSession(player.token, trackId);
      const response = await axiosConn.post(endpoint, payload, {
        headers: {
          Authorization: `Bearer ${player.token}`,
        },
      });
      const nextResult = isBuy
        ? response?.data?.data?.purchase
        : response?.data?.data?.tip;
      setResult(nextResult || {});
      onSuccess?.({
        mode,
        trackId,
        result: nextResult || {},
      });
    } catch (err) {
      setErrorMessage(
        getErrorMessage(
          err,
          isBuy
            ? 'Purchase could not be completed. Please try again.'
            : 'Tip could not be sent. Please try again.',
        ),
      );
    } finally {
      setSubmitting(false);
    }
  };

  const backdropClassName = [
    'hitlab-radio-support-backdrop',
    variant === 'dock' ? 'hitlab-radio-support-backdrop--dock' : '',
  ]
    .filter(Boolean)
    .join(' ');
  const sheetClassName = [
    'hitlab-radio-support-sheet',
    variant === 'dock' ? 'hitlab-radio-support-sheet--dock' : '',
  ]
    .filter(Boolean)
    .join(' ');

  return (
    <div
      className={backdropClassName}
      onMouseDown={(event) => {
        if (event.target === event.currentTarget) onClose?.();
      }}
    >
      <section
        className={sheetClassName}
        role="dialog"
        aria-modal="true"
        aria-labelledby="hitlab-radio-support-title"
        tabIndex="-1"
        ref={dialogRef}
      >
        <button
          type="button"
          className="hitlab-radio-support-close"
          onClick={onClose}
          aria-label="Close JGK support"
        >
          <X aria-hidden="true" size={17} />
        </button>

        <p className="hitlab-kicker">JGK artist support</p>
        <h2 id="hitlab-radio-support-title">{title}</h2>
        <p>{copy}</p>
        <div className="hitlab-radio-support-track">
          <strong>{trackTitle}</strong>
          <span>{artistName}</span>
        </div>

        {!player?.token ? (
          <div className="hitlab-radio-support-state">
            <h3>Sign in to support this record with JGK.</h3>
            <button type="button" className="hitlab-primary-btn" onClick={onLogin}>
              Sign In
            </button>
            <button
              type="button"
              className="hitlab-secondary-btn"
              onClick={onClose}
            >
              Keep listening
            </button>
          </div>
        ) : result ? (
          <div className="hitlab-radio-support-state" role="status">
            <h3>{isBuy ? result.message || 'Purchased with JGK.' : 'Tip sent.'}</h3>
            {isBuy ? (
              <>
                <DownloadLink href={cleanText(result.downloadUrl, 2048)} />
                {!result.downloadUrl && result.alreadyPurchased ? (
                  <p>
                    {result.downloadAvailable || downloadAvailable
                      ? 'Download link already issued.'
                      : 'MP3 download already used.'}
                  </p>
                ) : null}
              </>
            ) : null}
          </div>
        ) : showPurchasedState ? (
          <div className="hitlab-radio-support-state" role="status">
            <h3>Purchased.</h3>
            <p>
              {downloadAvailable
                ? 'Download link already issued.'
                : 'MP3 download already used.'}
            </p>
          </div>
        ) : supportUnavailable ? (
          <div className="hitlab-radio-support-state" role="alert">
            <h3>{supportUnavailable}</h3>
          </div>
        ) : (
          <form
            className="hitlab-radio-support-form"
            onSubmit={(event) => {
              event.preventDefault();
              void submit();
            }}
          >
            {isBuy ? (
              <div className="hitlab-radio-support-price">
                <span>Price</span>
                <strong>{priceJgk.toLocaleString()} JGK</strong>
              </div>
            ) : (
              <fieldset className="hitlab-radio-support-amounts">
                <legend>Amount</legend>
                <div>
                  {TIP_AMOUNTS_JGK.map((amount) => (
                    <button
                      key={amount}
                      type="button"
                      className={amountJgk === amount ? 'is-selected' : ''}
                      onClick={() => setAmountJgk(amount)}
                    >
                      {amount} JGK
                    </button>
                  ))}
                </div>
              </fieldset>
            )}

            {errorMessage ? (
              <div className="hitlab-radio-error" role="alert">
                {errorMessage}
              </div>
            ) : null}

            <button
              type="submit"
              className="hitlab-primary-btn"
              disabled={submitting || (isBuy && priceJgk <= 0)}
            >
              {submitting ? 'Processing' : confirmLabel}
            </button>
          </form>
        )}
      </section>
    </div>
  );
}
